import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { Popup, DragAndDrop, Button, Icon, PhotoEditor, Text } from 'components'

import { DndError } from 'utils/constants'
import { resolveDndErrorMessage } from 'utils/generic'

import { CropShape, Size } from '../PhotoEditor/types'
import { getCroppedImage } from '../PhotoEditor/helper'
import { DragDropFileRejection, DragDropEvent } from '../DragAndDrop/interfaces'

type DragDropProps = {
  accept?: string | Array<string>
  maxSize?: number
  multiple?: boolean
  onDrop: (acceptFiles: Array<File>, rejectedFiles: Array<DragDropFileRejection>, event: DragDropEvent) => void
}

type ModalProps = {
  footer?: React.ReactNode
  isSubmitting?: boolean
  isVisible: boolean
  onClose: () => void
  title?: React.ReactNode
  width?: string
}

const MIN_WIDTH = '650px'

const DragDropSection = (props: DragDropProps) => {
  const { t } = useTranslation()
  const [error, setError] = useState<{ filename: string; message: string }>()

  const handleOnDrop = (
    acceptFiles: Array<File>,
    rejectedFiles: Array<DragDropFileRejection>,
    event: DragDropEvent,
  ) => {
    if (props.onDrop) {
      props.onDrop(acceptFiles, rejectedFiles, event)
    }

    handleDropError(rejectedFiles)
  }

  const handleDropError = (rejectedFiles: Array<DragDropFileRejection>) => {
    if (rejectedFiles.length > 0) {
      const tempError: { filename: string; message: string } = { filename: '', message: '' }

      rejectedFiles.forEach((f) => {
        f.errors.forEach((i) => {
          if (tempError.message === '') {
            tempError.filename = f.file.name

            switch (i.code) {
              case DndError['file-too-large']:
                tempError.message = props.maxSize
                  ? t('file_exceeds_limit_error', { size: props.maxSize / 1024 / 1024 })
                  : t('file_too_big_error')
                break
              default:
                tempError.message = resolveDndErrorMessage(i.code)
            }
          }
        })
      })

      setError(tempError)
      return
    }

    setError(undefined)
  }

  return (
    <DragDropWrapper>
      <DragAndDrop {...props} onDrop={handleOnDrop} defaultViewHeight="400px">
        <Container>
          {error?.message ? (
            <ErrorContentWrapper>
              <Icon icon={['fal', 'exclamation-triangle']} fontSize={50} color="red" />
              <ErrorFileContent>
                <ErrorIcon icon={['fal', 'file-upload']} fontSize={36} color="gray4" />
                <Text fontSize={16}>{error.filename}</Text>
              </ErrorFileContent>
              <Text color="red" fontSize={14} fontWeight="medium" mb="16px">
                {error.message}
              </Text>
              <Button variant="outline">file_try_another_btn</Button>
            </ErrorContentWrapper>
          ) : (
            <ContentWrapper>
              <Icon icon={['fal', 'file-upload']} fontSize={60} color="gray4" />
              <ContentInfo>
                <Text fontWeight="medium" lineHeight="24px" color="gray7">
                  dnd_section_lbl
                </Text>
                <Button variant="outline">browse_files_btn</Button>
              </ContentInfo>
            </ContentWrapper>
          )}
        </Container>
      </DragAndDrop>
    </DragDropWrapper>
  )
}

const UploadModal: React.FC<DragDropProps & ModalProps> = (props) => {
  const { width, isVisible, onClose, title, ...dragDropProps } = props

  return isVisible ? (
    <Popup closePopUp={onClose} width={width} minWidth={MIN_WIDTH} title={title}>
      <DragDropSection {...dragDropProps} />
    </Popup>
  ) : null
}

type UploadAndCropModalProps = Omit<DragDropProps, 'multiple' | 'onDrop'> &
  Omit<ModalProps, 'onClose'> & {
    containerSize?: Size
    cropShape?: CropShape
    cropSize?: Size
    labelPrimary: string
    labelSecondary: string
    onClickPrimary: (
      croppedImage: { objectURL: string; fileObj: File },
      croppedAreaSize: Size,
      resetUpload: () => void,
    ) => void
    onClickSecondary: () => void
  }

const UploadAndCropModal: React.FC<UploadAndCropModalProps> = ({
  accept,
  containerSize,
  cropShape,
  cropSize,
  isSubmitting,
  isVisible,
  labelPrimary,
  labelSecondary,
  onClickPrimary,
  onClickSecondary,
  title,
  width,
  ...dragDropProps
}) => {
  const [acceptFile, setAcceptFile] = useState<null | string>()
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(undefined)
  const [croppedAreaSize, setCroppedAreaSize] = useState<Size>({ width: 0, height: 0 })

  const onDrop = (acceptFiles: Array<File>, _rejectedFiles: Array<DragDropFileRejection>, _event: DragDropEvent) => {
    if (acceptFiles.length > 0) {
      const file = acceptFiles[0]

      const reader = new FileReader()
      reader.onload = () => {
        setAcceptFile(reader.result?.toString())
      }
      reader.readAsDataURL(file)
    }
  }

  const resetUpload = () => {
    setAcceptFile(undefined)
  }

  const onCropComplete = useCallback((_, croppedAreaPixels, croppedAreaSize) => {
    setCroppedAreaPixels(croppedAreaPixels)
    setCroppedAreaSize(croppedAreaSize)
  }, [])

  const handleClickPrimary = async () => {
    if (acceptFile) {
      const croppedImage = await getCroppedImage(acceptFile, croppedAreaPixels)
      onClickPrimary(croppedImage, croppedAreaSize, resetUpload)
    }
  }

  const handleClickSecondary = () => {
    resetUpload()
    onClickSecondary()
  }

  const imagesOnly = useMemo(() => {
    if (accept) {
      const types = Array.isArray(accept) ? accept : accept.split(',')

      const imageFileTypes = types.filter((t) => {
        const [type] = t.split('/')
        return type.includes('image')
      })

      return imageFileTypes.join(',')
    }
  }, [accept])

  return isVisible ? (
    <Popup
      labelPrimary={labelPrimary}
      labelSecondary={labelSecondary}
      isDisabledPrimary={!acceptFile}
      isSubmitting={isSubmitting}
      minWidth={MIN_WIDTH}
      onClickPrimary={handleClickPrimary}
      onClickSecondary={handleClickSecondary}
      title={title}
      width={width}
    >
      {acceptFile ? (
        <PhotoEditorWrapper>
          <PhotoEditor
            imageUrl={acceptFile}
            containerSize={containerSize}
            cropShape={cropShape}
            cropSize={cropSize}
            onCropComplete={onCropComplete}
          />
          <Button disabled={isSubmitting} onClick={resetUpload} variant="outline">
            choose_different_image
          </Button>
        </PhotoEditorWrapper>
      ) : (
        <DragDropSection {...dragDropProps} accept={imagesOnly} onDrop={onDrop} />
      )}
    </Popup>
  ) : null
}

export default UploadModal
export { UploadAndCropModal }

const PhotoEditorWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 20px;
`

const Container = styled.div<{ width?: string; height?: string }>`
  width: ${(p) => p.width || '100%'};
  height: ${(p) => p.height || '100%'};
  border: 1px dotted ${(p) => p.theme.colors.defaultBorder};
  display: flex;
  justify-content: center;
  align-items: center;
`

const ContentWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const ContentInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 24px;
  justify-content: center;
  align-items: center;

  button {
    margin-top: 10px;
  }
`

const DragDropWrapper = styled.div`
  padding: 20px;
`

const ErrorContentWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const ErrorFileContent = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 16px 0;
`

const ErrorIcon = styled(Icon)`
  margin-right: 8px;
`
